import {SheetLocationImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetLocationImpl';
import {PredicateEvaluator} from '@mintdata-wsp/common/controllers/spreadsheet/functions/util/eval/PredicateEvaluator';
import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {
    FormulaKind,
    FunctionFeature,
    IFormulaExpression,
    ISheetAPI,
    ISheetLocation,
    ISheetRange,
    ISheetRangeIterator,
    ISheetValue,
    SheetLocationKey,
    SheetType
} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import bind from 'bind-decorator';

export class LogicalFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'logical';
    }

    @Function({
        title: 'if(logicalExpression, valueIfTrue, valueIfFalse)',
        tip: 'Returns one value if a logical expression is TRUE and another if it is FALSE.',
        example: 'IF(A2 = "foo", "A2 is foo", "A2 is not foo")',
        alias: 'IF',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Output frame',
                tip: 'Returns value'
            }
    })
    @bind
    public async if(@Param({
                        title: 'logicalExpression',
                        tip: 'An expression or reference to a cell containing an expression that represents some logical value, i.e. TRUE or FALSE.',
                        type: Type.Boolean
                    }) logicalExpression:ABoolean,
                    @Param({
                        title: 'valueIfTrue',
                        tip: 'The value the function returns if logicalExpression is TRUE.',
                        type: SheetType.FormulaExpression
                    }) valueIfTrue:IFormulaExpression,
                    @Param({
                        title: 'valueIfFalse',
                        tip: 'The value the function returns if logicalExpression is FALSE. If omitted, FALSE will be returned if expression is FALSE',
                        type: SheetType.FormulaExpression,
                        optional: true
                    }) valueIfFalse:IFormulaExpression):Promise<IValue>
    {
        if( logicalExpression.value )
            return await valueIfTrue.eval();

        return valueIfFalse != undefined ? await valueIfFalse.eval() : ABoolean.false();
    }

    @Function({
        title: 'iferror(expression, valueIfError)',
        tip: 'Returns result if an expression does not return an error and another if it ends with Error.',
        example: 'IFERROR(A2/B1, "Division by zero")',
        alias: 'IFERROR',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Returns value',
                tip: 'Returns value'
            }
    })
    @bind
    public iferror(@Param({
                       title: 'expression',
                       tip: 'An expression to be evaluated.',
                       type: SheetType.AnyIValue
                   }) expression:any,
                   @Param({
                       title: 'valueIfError',
                       tip: 'The value the function returns if expression evaluates to an error. The following error types are evaluated: #N/A, #VALUE!, #REF!, #DIV/0!, #NUM!, #NAME?, #NULL!',
                       type: SheetType.AnyIValue
                   }) valueIfError:any):any
    {
        if( Type.isInstanceOf(valueIfError, Type.Empty) ) valueIfError = new AInteger(0);
        return Type.isInstanceOf(expression, Type.Error) ? valueIfError : expression;
    }

    @Function({
        title: 'and(logicalExpressions...)',
        tip: 'Returns TRUE if all expressions are TRUE otherwise return FALSE',
        example: 'AND(A2 > 5, A2 > 10, A5:B8)',
        alias: 'AND',
        icon: '',
        returns:
            {
                type: Type.Boolean,
                title: 'Value',
                tip: 'Returns boolean value'
            }
    })
    @bind
    public async and(@Param({
                         title: 'api',
                         tip: 'api',
                         type: SheetType.SheetAPI
                     }) api:ISheetAPI,
                     @Param({
                         title: 'expressions',
                         tip: 'Expression list.',
                         type: SheetType.FormulaExpression,
                         repeatable: true
                     }) expressions:Array<IFormulaExpression>):Promise<ABoolean | AError>
    {
        for( const expression of expressions )
        {
            const values = await this._collectIValues(api, expression);
            for( const value of values )
            {
                const coercedValue = CoercionUtils.coerce(value, Type.Boolean, null);
                if( !Type.isInstanceOf(coercedValue, Type.Boolean) )
                    return EvalErrors.Value;

                if( (coercedValue as ABoolean).isEqualTo(ABoolean.false()) )
                    return ABoolean.false();
            }
        }

        return ABoolean.true();
    }

    @Function({
        title: 'or(logicalExpressions...)',
        tip: 'Returns TRUE if any of the expressions is TRUE otherwise return FALSE',
        example: 'OR(A2 > 5, A2 > 10, A5:B8)',
        alias: 'OR',
        icon: '',
        returns:
            {
                type: Type.Boolean,
                title: 'Value',
                tip: 'Returns boolean value'
            }
    })
    @bind
    public async or(@Param({
                        title: 'api',
                        tip: 'api',
                        type: SheetType.SheetAPI
                    }) api:ISheetAPI,
                    @Param({
                        title: 'expressions',
                        tip: 'Expression list.',
                        type: SheetType.FormulaExpression,
                        repeatable: true
                    }) expressions:Array<IFormulaExpression>):Promise<ABoolean | AError>
    {
        for( const expression of expressions )
        {
            const values = await this._collectIValues(api, expression);
            for( const value of values )
            {
                const coercedValue = CoercionUtils.coerce(value, Type.Boolean, null);
                if( !Type.isInstanceOf(coercedValue, Type.Boolean) )
                    return EvalErrors.Value;

                if( (coercedValue as ABoolean).isEqualTo(ABoolean.true()) )
                    return ABoolean.true();
            }
        }

        return ABoolean.false();
    }

    private async _collectIValues(api:ISheetAPI, expression:IFormulaExpression):Promise<Array<IValue>>
    {
        const evalValue:IValue | ISheetValue = await expression.eval();
        if( Type.isInstanceOfIValue(evalValue) )
            return [evalValue as IValue];

        let convertedValues:IValue | IValue[] = api.getSheetContext().convertToIValue(evalValue);
        if( !Array.isArray(convertedValues) )
            convertedValues = [convertedValues as IValue];

        const result = [];
        for( const convertedValue of convertedValues )
        {
            if( !Type.isInstanceOf(convertedValue, Type.Frame) )
                result.push(convertedValue);
            else
            {
                const frame = convertedValue as IFrame;
                frame.getDataUnsafe().forEach(row =>
                    row.forEach(cellValue => result.push(cellValue))
                );
            }
        }
        return result;
    }

    @Function({
        title: 'toggle(initialValue)',
        tip: 'Alternates between returning TRUE and FALSE.',
        example: 'TOGGLE(TRUE)',
        alias: 'TOGGLE',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'The opposite of current value',
                tip: 'Returns TRUE or FALSE value'
            }
    })
    @bind
    public toggle(@Param({title: 'api', tip: 'api', type: SheetType.SheetAPI}) api:ISheetAPI,
                  @Param({
                      title: 'initialValue',
                      tip: 'An expression or reference to a cell containing an expression that represents some logical value, i.e. TRUE or FALSE.',
                      type: Type.Boolean,
                      optional: true
                  }) initialValue:ABoolean):ABoolean
    {
        const sheetCtx = api.getSheetContext();
        const currentCellLocation = sheetCtx.getCellLocation();

        let cellValue = sheetCtx.getCellValue(currentCellLocation);

        if( !cellValue || !(cellValue.getType() == Type.Boolean) )
        {
            const value = initialValue ? initialValue.value : false;
            return ABoolean.fromBoolean(value);
        }
        else
            return ABoolean.fromBoolean(!(cellValue as ABoolean).value);
    }

    @Function({
        title: 'not(aBoolean)',
        tip: 'Alternates between returning TRUE and FALSE.',
        example: 'NOT(TRUE)',
        alias: 'NOT',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'The opposite of current value',
                tip: 'Returns TRUE or FALSE value'
            }
    })
    @bind
    public not(@Param({
        title: 'value',
        tip: 'An expression or reference to a cell containing an expression that represents some logical value, i.e. TRUE or FALSE.',
        type: Type.Boolean,
        optional: true
    }) aBoolean:ABoolean):ABoolean
    {
        return ABoolean.fromBoolean(!aBoolean.value);
    }

    @Function({
        title: 'countif(criteria_range, criteria)',
        tip: 'Counts the number of cells that meet a criterion; for example, to count the number of times a particular city appears in a customer list',
        example: 'COUNTIF(A1:A12,"New York")',
        alias: 'COUNTIF',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Count',
                tip: 'Number of times criteria is met'
            }
    })
    @bind
    public async countif(@Param({
                             title: 'api',
                             tip: 'api',
                             type: SheetType.SheetAPI
                         }) api:ISheetAPI,
                         @Param({
                             title: 'criteria_range',
                             tip: 'The range in which to evaluate the associated criteria',
                             type: SheetType.SheetRange
                         }) criteriaRange:ISheetRange,
                         @Param({
                             title: 'criteria',
                             tip: 'The criteria in the form of a number, expression, cell reference, or text that define which cells will be counted. Criteria aren\'t case sensitive. In other words, the string "apples" and the string "APPLES" will match the same cells. Wildcard characters —the question mark (?) and asterisk (*)—can be used in criteria. A question mark matches any single character. An asterisk matches any sequence of characters. If you want to find an actual question mark or asterisk, type a tilde (~) in front of the character. ',
                             type: Type.String
                         }) criteria:AString):Promise<AInteger>
    {
        const matches = await this._matchUsingRange(api, criteriaRange, criteria);
        return AInteger.fromNumber(matches.length);
    }

    @Function({
        title: 'COUNTIFS(criteria_range1, criteria1,[criteria_range2, criteria2], ...)',
        tip: 'Applies criteria to cells across multiple ranges and counts the number of times all criteria are met',
        example: 'COUNTIFS(B1:B12,">10", C1:C12,"Tom")',
        alias: 'COUNTIFS',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Count',
                tip: 'Number of times all criteria are met'
            }
    })
    @bind
    public async countifs(@Param({
                              title: 'api',
                              tip: 'api',
                              type: SheetType.SheetAPI
                          }) api:ISheetAPI,
                          @Param({
                              title: 'criterias',
                              tip: 'Ranges and criterias to apply. All ranges must be of the same size. Criteria aren\'t case sensitive. In other words, the string "apples" and the string "APPLES" will match the same cells. Wildcard characters —the question mark (?) and asterisk (*)—can be used in criteria. A question mark matches any single character. An asterisk matches any sequence of characters. If you want to find an actual question mark or asterisk, type a tilde (~) in front of the character. ',
                              type: SheetType.Any,
                              repeatable: true
                          }) criterias:any[]):Promise<AInteger>
    {
        if( criterias.length === 2 )
        {
            const matches:Array<number> = await this._matchUsingRange(api, criterias[0], criterias[1]);

            return AInteger.fromNumber(matches.length);
        }
        else
        {
            const matches:Array<boolean> | AError = await this._matchUsingSeveralRanges(criterias, api);

            if( Type.isInstanceOf(matches, Type.Error) ) return matches as any;

            return AInteger.fromNumber((matches as Array<boolean>).filter(v => v).length);
        }
    }

    @Function({
        title: 'SUMIF(criteria_range, criteria)',
        tip: 'Applies criteria to cells across range and sum the values in a range that meet criteria specified',
        example: 'SUMIF(A1:A12,">10")',
        alias: 'SUMIF',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Sum',
                tip: 'Sum of the values in a range that meet criteria specified'
            }
    })
    @bind
    public async sumif(@Param({
                           title: 'api',
                           tip: 'api',
                           type: SheetType.SheetAPI
                       }) api:ISheetAPI,
                       @Param({
                           title: 'criteria_range',
                           tip: 'The range in which to evaluate the associated criteria',
                           type: SheetType.SheetRange
                       }) criteriaRange:ISheetRange,
                       @Param({
                           title: 'criteria',
                           tip: 'The criteria in the form of a number, expression, cell reference, or text that define which cells will be counted. Criteria aren\'t case sensitive. In other words, the string "apples" and the string "APPLES" will match the same cells. Wildcard characters —the question mark (?) and asterisk (*)—can be used in criteria. A question mark matches any single character. An asterisk matches any sequence of characters. If you want to find an actual question mark or asterisk, type a tilde (~) in front of the character.',
                           type: Type.String
                       }) criteria:AString,
                       @Param({
                           title: 'sum_range',
                           tip: 'Optional. The actual set of cells to sum. If omitted, range is used',
                           type: SheetType.SheetRange,
                           optional: true
                       }) oprange:ISheetRange):Promise<AInteger>
    {
        const matches:Array<number> = await this._matchUsingRange(api, criteriaRange, criteria, oprange);

        return ANumber.fromNumber(matches.reduce((a:number, b:number) =>
        {
            // const val = parseFloat((b as any).value);
            const val:number = b;
            return a + (isNaN(val) ? 0 : val);
        }, 0));
    }

    @Function({
        title: 'SUMIFS(sum_range, criteria_range1, criteria1,[criteria_range2, criteria2], ...)',
        tip: 'Adds all of its arguments that meet multiple criteria. For example, you would use SUMIFS to sum the number of retailers in the country who (1) reside in a single zip code and (2) whose profits exceed a specific dollar value',
        example: 'SUMIFS(A1:A12,B1:B12,">10", C1:C12,"Tom")',
        alias: 'SUMIFS',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Sum',
                tip: 'Sum of the values in a range that meet criterias specified'
            }
    })
    @bind
    public async sumifs(@Param({
                            title: 'api',
                            tip: 'api',
                            type: SheetType.SheetAPI
                        }) api:ISheetAPI,
                        @Param({
                            title: 'sum_range',
                            tip: 'Set of cells to sum.',
                            type: SheetType.SheetRange
                        }) oprange:ISheetRange,
                        @Param({
                            title: 'criterias',
                            tip: 'Ranges and criterias to apply. All ranges must be of the same size. Criteria aren\'t case sensitive. In other words, the string "apples" and the string "APPLES" will match the same cells. Wildcard characters —the question mark (?) and asterisk (*)—can be used in criteria. A question mark matches any single character. An asterisk matches any sequence of characters. If you want to find an actual question mark or asterisk, type a tilde (~) in front of the character. ',
                            type: SheetType.Any,
                            repeatable: true
                        }) criterias:any[]):Promise<any>
    {
        const matches:Array<boolean> | AError = await this._matchUsingSeveralRanges(criterias, api);

        // console.log(`Criteria Matches ${matches}`);
        if( Type.isInstanceOf(matches, Type.Error) ) return matches;

        const resultMatches:Array<IValue> = this._collectMatchesUsingOperationalRange(matches as Array<boolean>, api, oprange);

        return ANumber.fromNumber(resultMatches.reduce((a:number, b:IValue) =>
        {
            const val = parseFloat((b as any).value);
            return a + (isNaN(val) ? 0 : val);
        }, 0));
    }

    @Function({
        title: 'averageif(criteria_range, criteria)',
        tip: 'Returns the average (arithmetic mean) of all the cells in a range that meet a given criteria.',
        example: 'AVERAGEIF(A1:A12,">10")',
        alias: 'AVERAGEIF',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Average',
                tip: 'Average (arithmetic mean) of all the cells in a range that meet a given criteria'
            }
    })
    @bind
    public async averageif(@Param({
                               title: 'api',
                               tip: 'api',
                               type: SheetType.SheetAPI
                           }) api:ISheetAPI,
                           @Param({
                               title: 'criteria_range',
                               tip: 'The range in which to evaluate the associated criteria',
                               type: SheetType.SheetRange
                           }) criteriaRange:ISheetRange,
                           @Param({
                               title: 'criteria',
                               tip: 'The criteria in the form of a number, expression, cell reference, or text that define which cells will be counted. Criteria aren\'t case sensitive. In other words, the string "apples" and the string "APPLES" will match the same cells. Wildcard characters —the question mark (?) and asterisk (*)—can be used in criteria. A question mark matches any single character. An asterisk matches any sequence of characters. If you want to find an actual question mark or asterisk, type a tilde (~) in front of the character. ',
                               type: Type.String
                           }) criteria:AString,
                           @Param({
                               title: 'average_range',
                               tip: 'Optional. The actual set of cells to average. If omitted, range is used',
                               type: SheetType.SheetRange,
                               optional: true
                           }) oprange:ISheetRange):Promise<any>
    {
        const matches:Array<number> = await this._matchUsingRange(api, criteriaRange, criteria, oprange);
        const positives:Array<number> = matches.filter((v) => v >= 0);

        if( positives.length == 0 ) return EvalErrors.Div0;

        let valid:number = 0;

        return ANumber.fromNumber(positives.reduce((a:number, b:number) =>
        {
            // const val = parseFloat((b as any).value);
            const val:number = b;

            if( !isNaN(val) ) valid++;

            return a + (isNaN(val) ? 0 : val);
        }, 0) / valid);
    }

    @Function({
        title: 'AVERAGEIFS(sum_range, criteria_range1, criteria1,[criteria_range2, criteria2], ...)',
        tip: 'Returns the average (arithmetic mean) of all cells that meet multiple criterias',
        example: 'AVERAGEIFS(A1:A12,B1:B12,">10", C1:C12,"Tom")',
        alias: 'AVERAGEIFS',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Average',
                tip: 'Average of the values in a range that meet criterias specified'
            }
    })
    @bind
    public async averageifs(@Param({
                                title: 'api',
                                tip: 'api',
                                type: SheetType.SheetAPI
                            }) api:ISheetAPI,
                            @Param({
                                title: 'count_range',
                                tip: 'Set of cells to calculate average.',
                                type: SheetType.SheetRange
                            }) oprange:ISheetRange,
                            @Param({
                                title: 'criterias',
                                tip: 'Ranges and criterias to apply. All ranges must be of the same size. Criteria aren\'t case sensitive. In other words, the string "apples" and the string "APPLES" will match the same cells. Wildcard characters —the question mark (?) and asterisk (*)—can be used in criteria. A question mark matches any single character. An asterisk matches any sequence of characters. If you want to find an actual question mark or asterisk, type a tilde (~) in front of the character.',
                                type: SheetType.Any,
                                repeatable: true
                            }) criterias:any[]):Promise<any>
    {
        const matches:Array<boolean> | AError = await this._matchUsingSeveralRanges(criterias, api);

        if( Type.isInstanceOf(matches, Type.Error) ) return matches;

        const resultMatches:Array<IValue> = this._collectMatchesUsingOperationalRange(matches as Array<boolean>, api, oprange);

        if( resultMatches.length == 0 ) return EvalErrors.Div0;

        let valid:number = 0;

        return ANumber.fromNumber(resultMatches.reduce((a:number, b:IValue) =>
        {
            const val = parseFloat((b as any).value);

            if( !isNaN(val) ) valid++;

            return a + (isNaN(val) ? 0 : val);
        }, 0) / valid);
    }

    @Function({
        title: 'COPY_STYLE_IF(logical_expression, cell_ref_if_true, cell_ref_if_false, to_range)',
        tip: 'Applies styles from a particular cell to the range.',
        example: 'COPY_STYLE_IF(A2 > 5, B3, C4, D12:H21)',
        alias: 'COPY_STYLE_IF',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                type: Type.Boolean,
                title: 'Value',
                tip: 'Returns boolean value'
            }
    })
    @bind
    public copyStyleIf(@Param({
                           title: 'api',
                           tip: 'api',
                           type: SheetType.SheetAPI
                       }) api:ISheetAPI,
                       @Param({
                           title: 'logical_expression',
                           tip: 'An expression or reference to a cell containing an expression that represents some logical value, i.e. TRUE or FALSE.',
                           type: Type.Boolean
                       }) logicalExpression:ABoolean,
                       @Param({
                           title: 'cell_ref_if_true',
                           tip: 'The cell whose styles will be applied if expression is TRUE',
                           type: SheetType.SheetLocation,
                           optional: true,
                       }) cellRefIfTrue:ISheetLocation,
                       @Param({
                           title: 'cell_ref_if_false',
                           tip: 'The cell whose styles will be applied if expression is FALSE',
                           type: SheetType.SheetLocation,
                           optional: true,
                       }) cellRefIfFalse:ISheetLocation,
                       @Param({
                           title: 'to_range',
                           tip: 'The range in which styles will be applied',
                           type: SheetType.SheetRange
                       }) toRange:ISheetRange):ABoolean
    {
        const fromCellRef:ISheetLocation = logicalExpression.value ? cellRefIfTrue : cellRefIfFalse;

        if( fromCellRef && fromCellRef.sheetId )
        {
            const ctx = api.getSheetContext();
            const fromCellSettings = ctx.getCellSettings(fromCellRef);
            const fromCellFormat = ctx.getCellFormat(fromCellRef);

            const iteratorOverOperationRange:ISheetRangeIterator = ctx.getRangeIterator(toRange);

            for( let i = 0; i < iteratorOverOperationRange.numCells; i++ )
            {
                let location:ISheetLocation = iteratorOverOperationRange.getNthCellLocation(i);

                ctx.setCellSettings(location, fromCellSettings);
                ctx.setCellFormat(location, fromCellFormat && fromCellFormat.id);
            }

            return ABoolean.true();
        }

        return ABoolean.false();
    }

    /**
     * Applies some function (depending on client function) to every value of some operation range where some passed
     * criteria is evaluated to true
     * @param api
     * @param criteriaRange
     * @param criteria
     * @param func
     * @param operationRange
     * @private
     */
    private async _matchUsingRange(api:ISheetAPI, criteriaRange:ISheetRange, criteria:IValue, operationRange?:ISheetRange):Promise<Array<number>>
    {
        const values:Map<SheetLocationKey, IValue> = api.getSheetContext().getRangeValues(
            operationRange || criteriaRange
        );

        let valueExtractor:(sheetLocation:ISheetLocation) => number = (location) =>
        {

            // const cellValue = api.getSheetContext().getCellValue(location);
            const cellValue:IValue = values.get(location.asKey());

            if( Type.isInstanceOf(cellValue, Type.Integer) )
                return (cellValue as AInteger).value;
            else if( Type.isInstanceOf(cellValue, Type.Number) )
                return (cellValue as ANumber).value;
            else
                return 0;
        };

        const ctx = api.getSheetContext();
        let iteratorOverOperationRange:ISheetRangeIterator = ctx.getRangeIterator(operationRange || criteriaRange);
        const locationToIteratorPosition:Map<number, ISheetLocation> = new Map<number, ISheetLocation>();

        for( let i = 0; i < iteratorOverOperationRange.numCells; i++ )
        {
            let location:ISheetLocation = iteratorOverOperationRange.getNthCellLocation(i);

            if( values.has(location.asKey()) )
                locationToIteratorPosition.set(i, location);
        }

        let evaluationResults:Array<boolean> = await this._evaluateCriteriaOverRange(api, criteriaRange, criteria);
        let resultArray:Array<number> = [];

        for( let i = 0; i < evaluationResults.length; i++ )
        {
            let result:boolean = evaluationResults[i];

            if( result )
            {
                let location:ISheetLocation = locationToIteratorPosition.get(i);
                resultArray.push(valueExtractor(location));
            }
        }

        return resultArray;
    }

    /**
     * Function to evaluate criteria over some frame. Returns map where keys are slot locations and values are promises,
     * that are resolved to TRUE or FALSE depending on result of evaluation
     * @param api
     * @param criteriaFrame
     * @param criteria
     */
    private async _evaluateCriteriaOverFrame(api:ISheetAPI, criteriaFrame:IFrame, criteria:IValue):Promise<Array<boolean>>
    {
        const ctx = api.getSheetContext();

        // console.info('criteria:', criteria);

        const promisesArray = criteriaFrame.getColumn(criteriaFrame.getFieldNames()[0])
            .rows
            .map(async (cellValue:IValue):Promise<boolean> =>
            {
                // console.info('cellValue:', cellValue);
                let formula:string;

                if( Type.isInstanceOf(criteria, Type.String) )
                {
                    const criteriaValue = (criteria as AString).value;

                    if( criteriaValue.search(/([><=\d])/) === -1 )
                        return PredicateEvaluator.testPossibleWildcardWithRegexp(cellValue.toString(), criteriaValue); //we have just string value
                                                                                                                       // like "apple",a??le or appl*
                                                                                                                       // so all we want is to check
                                                                                                                       // slot value against it
                    else if( criteriaValue.search(/([><=])/) === -1 && criteriaValue.search(/([\d])/) !== -1 )
                        formula = '=' + cellValue.toString() + '=' + criteriaValue; //have some digits, but does not have comparison
                    else
                        formula = '=' + cellValue.toString() + criteriaValue; //we have something like ">10"
                }
                else if( Type.isInstanceOf(criteria, Type.Integer) || Type.isInstanceOf(criteria, Type.Number) )
                {
                    const criteriaValue = (criteria as ANumber).value;
                    formula = '=' + cellValue.toString() + '=' + criteriaValue;
                }
                else
                    return false;

                const iValue:IValue = await ctx.evalFormula(formula, FormulaKind.A1(), new Map<string, IValue>(), false);

                return Type.isInstanceOf(iValue, Type.Boolean) && (iValue as ABoolean).value;
            });

        return Promise.all(promisesArray);
    }

    /**
     * Function to evaluate criteria over some range. Returns map where keys are slot locations and values are promises,
     * that are resolved to TRUE or FALSE depending on result of evaluation
     * @param api
     * @param criteriaRange
     * @param criteria
     */
    private async _evaluateCriteriaOverRange(api:ISheetAPI, criteriaRange:ISheetRange, criteria:IValue)
    {
        const ctx = api.getSheetContext();
        const rangeIterator = ctx.getRangeIterator(criteriaRange);
        const slotArray:ISheetLocation[] = [];
        //console.log(`CriteriaRange: ${criteriaRange}`);

        for( let i = 0; i < rangeIterator.numCells; i++ )
        {
            let location:ISheetLocation = rangeIterator.getNthCellLocation(i);
            slotArray.push(location);
        }

        const cellValues:Map<SheetLocationKey, IValue> = api.getSheetContext().getRangeValues(criteriaRange);

        const promisesArray = slotArray.map(async (location) =>
        {
            let formula:string;
            // const cellValue:IValue = api.getSheetContext().getCellValue(location);
            const cellValue:IValue = cellValues.has(location.asKey()) ? cellValues.get(location.asKey()) : AEmpty.Value;

            // console.info('cellValue:', cellValue);
            if( Type.isInstanceOf(criteria, Type.String) )
            {
                const criteriaValue = (criteria as AString).value;
                //console.info(`criteria value:`, criteriaValue);

                if( criteriaValue.search(/([><=\d])/) === -1 )
                    return PredicateEvaluator.testPossibleWildcardWithRegexp(cellValue.toString(), criteriaValue); //we have just string value like
                                                                                                                   // "apple",a??le or appl* so all
                                                                                                                   // we want is to check slot value
                                                                                                                   // against it
                else if( criteriaValue.search(/([><=])/) === -1 && criteriaValue.search(/([\d])/) !== -1 )
                    formula = '=' + cellValue.toString() + '=' + criteriaValue; //have some digits, but does not have comparison
                else
                    formula = '=' + cellValue.toString() + criteriaValue; //we have something like ">10"
            }
            else if( Type.isInstanceOf(criteria, Type.Integer) || Type.isInstanceOf(criteria, Type.Number) )
            {
                const criteriaValue = (criteria as ANumber).value;
                //console.info(`criteria value:`, criteriaValue);
                formula = '=' + cellValue.toString() + '=' + criteriaValue;
            }
            else
            {
                //console.info(`Nothing matched! Type: ${criteria.getType().toString()}`);
                return false;
            }
            //console.log(`Formula: ${formula}`);
            const iValue:IValue = await ctx.evalFormula(formula, FormulaKind.A1(), new Map<string, IValue>(), false);

            return Type.isInstanceOf(iValue, Type.Boolean) && (iValue as ABoolean).value;
        });
        return await Promise.all(promisesArray);
    }

    /**
     * Run criteria evaluation logic over single array containing ranges and criteria in mixed manner (range-criteria-range-criteria...)
     * Each criteria should run only against range to the right
     * @param criteriasAndRanges
     * @param api
     * @private
     */
    private async _matchUsingSeveralRanges(criteriasAndRanges:any[], api:ISheetAPI):Promise<Array<boolean> | AError>
    {
        const rangesArray:Array<ISheetRange | IFrame> = [];
        const criteriaArray:Array<IValue> = [];

        if( undefined != criteriasAndRanges )
        {
            for( let i = 0; i < criteriasAndRanges.length; i++ )
            {
                const value = criteriasAndRanges[i];

                if( i % 2 == 0 )
                    rangesArray.push(value); //even number - it must be a range
                else //else it is a criteria
                if( Type.isInstanceOfIValue(value) )
                    criteriaArray.push(value as IValue);
                else if( SheetLocationImpl.isSheetLocation(value) )
                    criteriaArray.push(api.getSheetContext().getCellValue(value as ISheetLocation));
                else
                    return new AError('#VALUE!');
            }
        }

        //makes sense to continue if a) each criteria has its range to evaluate against, b) all ranges are of equal size
        if( rangesArray.length !== criteriaArray.length )
            return new AError('#VALUE!');

        let diffInColumns:number;
        let diffInRows:number;

        if( Type.isInstanceOf(rangesArray[0], Type.Frame) )
        {
            const frameToCompareWith:IFrame = rangesArray[0] as IFrame;
            diffInColumns = frameToCompareWith.fieldCount - 1;
            diffInRows = frameToCompareWith.getRowsCount() - 1;
        }
        else
        {
            const rangeToCompareWith:ISheetRange = rangesArray[0] as ISheetRange;
            diffInColumns = rangeToCompareWith.to.columnIndex - rangeToCompareWith.from.columnIndex;
            diffInRows = rangeToCompareWith.to.rowIndex - rangeToCompareWith.from.rowIndex;
        }

        const checkAllRanges:boolean = rangesArray.every(rng =>
        {
            if( Type.isInstanceOf(rng, Type.Frame) )
            {
                const frame:IFrame = rng as IFrame;
                const isColumnsSame = frame.fieldCount - 1 === diffInColumns;
                const isRowsSame = frame.getRowsCount() - 1 === diffInRows;

                return isColumnsSame && isRowsSame;
            }
            else
            {
                const range:ISheetRange = rng as ISheetRange;
                const isColumnsSame = range.to.columnIndex - range.from.columnIndex === diffInColumns;
                const isRowsSame = range.to.rowIndex - range.from.rowIndex === diffInRows;

                return isColumnsSame && isRowsSame;
            }
        });

        if( !checkAllRanges )
            return new AError('#VALUE!');

        //First we should reduce ranges to arrays of booleans. Got an array of vectors
        const rangeEvalResults:Array<Array<boolean>> = [];

        for( let i = 0; i < rangesArray.length; i++ )
            if( Type.isInstanceOf(rangesArray[i], Type.Frame) )
                rangeEvalResults.push(await this._evaluateCriteriaOverFrame(api, rangesArray[i] as IFrame, criteriaArray[i]));
            else
                rangeEvalResults.push(await this._evaluateCriteriaOverRange(api, rangesArray[i] as ISheetRange, criteriaArray[i]));

        //Then reduce stringwise (1 value from each vector as a row)
        let reducedBooleans:Array<boolean> = [];
        let firstColumn:Array<boolean> = rangeEvalResults[0]; //all have the same length

        for( let i = 0; i < firstColumn.length; i++ )
        {
            let value:boolean = true;

            for( let j = 0; j < rangeEvalResults.length; j++ )
                value = value && rangeEvalResults[j][i];

            reducedBooleans.push(value);
        }

        return reducedBooleans;
    }

    private _collectMatchesUsingOperationalRange(reducedBooleans:Array<boolean>, api:ISheetAPI, oprange:ISheetRange):Array<IValue>
    {
        const values:Map<SheetLocationKey, IValue> = api.getSheetContext().getRangeValues(oprange);
        const matches:Array<IValue> = [];

        for( let i = 0; i < reducedBooleans.length; i++ )
        {
            if( reducedBooleans[i] )
            {
                const ctx = api.getSheetContext();
                let iteratorOverOperationRange = ctx.getRangeIterator(oprange);

                // matches.push((ctx.getCellValue(iteratorOverOperationRange.getNthCellLocation(i)) as ANumber));
                matches.push(
                    values.has(iteratorOverOperationRange.getNthCellLocation(i).asKey())
                        ? values.get(iteratorOverOperationRange.getNthCellLocation(i).asKey())
                        : AEmpty.Value
                );
            }
        }

        return matches;
    }
}
